import { z } from 'zod';
import { taskPrioritySchema, taskTypeSchema } from './tasks.schema';

/**
 * The specialist / lead workbench (Sprint 2 S2-05, KB screen "Specialist Daily Command Workbench").
 * The columns are derived from what already exists on the task, never stored, so nothing can drift:
 *  - `todo`: assigned, nothing started (0%, no milestone ticked, no version sent);
 *  - `in_progress`: work has started;
 *  - `changes_requested`: a lead sent the latest version back, or the client asked for changes;
 *  - `ready_for_qa`: a version is waiting on a lead;
 *  - `with_client`: approved by a lead and waiting on the client (still counts against capacity).
 */
export const WORKBENCH_COLUMNS = ['todo', 'in_progress', 'changes_requested', 'ready_for_qa', 'with_client'] as const;
export const workbenchColumnSchema = z.enum(WORKBENCH_COLUMNS);
export type WorkbenchColumn = z.infer<typeof workbenchColumnSchema>;

export const workbenchCardSchema = z.object({
  taskId: z.string(),
  title: z.string(),
  taskType: taskTypeSchema,
  priority: taskPrioritySchema,
  /** The raw task status. */
  status: z.string(),
  column: workbenchColumnSchema,
  projectId: z.string(),
  projectName: z.string(),
  businessName: z.string().nullable(),
  /** The specialist's slider, 0-100. */
  progress: z.number().int().min(0).max(100),
  milestoneDone: z.number().int(),
  milestoneTotal: z.number().int(),
  deadline: z.string().nullable(),
  assignedAt: z.string(),
  /** Effort in units ("2 u"). */
  workUnits: z.number(),
  /** The lead on the task (a card in "Waiting for QA" is with them). Null when there is none. */
  lead: z.object({ id: z.string(), name: z.string() }).nullable(),
  /** The newest version sent to QA (null before the first); `sentAt` is when it last changed. */
  version: z.object({ number: z.number().int(), sentAt: z.string() }).nullable(),
  /** A fix this person proposed to a version that is live: it waits on a lead while the client keeps the live version. */
  fix: z.object({ liveVersion: z.number().int(), sentAt: z.string() }).nullable(),
  /** When the lead approved the version the client is reviewing ("Client reviewing · 1 d"). */
  publishedAt: z.string().nullable(),
  /** Why the card is in "Changes requested": who asked, when, the note, and for a client the round used. */
  sentBack: z
    .object({
      by: z.enum(['lead', 'client']),
      /** The lead's name, or the client's first name. */
      byName: z.string().nullable(),
      note: z.string().nullable(),
      at: z.string().nullable(),
      revision: z.object({ number: z.number().int(), of: z.number().int() }).nullable(),
    })
    .nullable(),
});
export type WorkbenchCard = z.infer<typeof workbenchCardSchema>;

export const workbenchSchema = z.object({
  /** The gauge: cards past "To do" (In progress, Changes requested, Waiting for QA, With client), against `agency_staff.max_active_tasks`. */
  capacity: z.object({ active: z.number().int(), max: z.number().int() }),
  /** The daily queue: soonest deadline first (undated last), then priority, then most recently touched. */
  items: z.array(workbenchCardSchema),
  counts: z.record(workbenchColumnSchema, z.number().int()),
});
export type Workbench = z.infer<typeof workbenchSchema>;
